import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Select,
  Input,
  message,
  Tag,
  Space,
  Radio,
  Alert,
  Progress,
  Dropdown,
  Segmented,
  Badge,
  Tooltip,
  type MenuProps,
  Row,
  Col,
  Grid,
} from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import {
  getSims,
  allocateSim,
  assignSimToAsset,
  assignSimToUser,
  removeSimAssignment,
  deallocateSim,
  simOperation,
  getOrganizations,
  getAssets,
  getUsers,
  UnifiedSim,
  Organization,
  Asset,
  User,
} from '../../services/adminService';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import LookupSelect from '../../components/LookupSelect';
import PageHelp from '../../components/PageHelp';
import PageHeader from '../../components/PageHeader';
import EditableFriendlyName from '../../components/EditableFriendlyName';
import ResponsiveTableOrCards from '../../components/ResponsiveTableOrCards';
import SimListCard from '../../components/SimListCard';
import type { SimListCardHandlers } from '../../components/SimListCard';
import ResponsiveModal from '../../components/ResponsiveModal';
import ProvisionEsimModal from '../../components/ProvisionEsimModal';
import EsimGuideLink from '../../components/esim/EsimGuideLink';
import { providerLabel } from '../../utils/providerLabel';
import {
  INVENTORY_TYPES,
  DEFAULT_TYPE_KEY,
  getInventoryType,
  countByType,
  isValidTypeKey,
  type InventoryTypeKey,
} from './inventoryTypes';

const FRIENDLY_NAME_EDIT_ROLES = [
  'super_admin',
  'reseller_admin',
  'reseller_support',
  'customer_admin',
  'asset_manager',
];

const SimInventory: React.FC = () => {
  const { role } = useAuth();
  const canOperate = role === 'super_admin' || role === 'reseller_admin';
  const canEditFriendlyName = !!role && FRIENDLY_NAME_EDIT_ROLES.includes(role);
  const [printMode, setPrintMode] = useState(false);
  const screens = Grid.useBreakpoint();

  // KAN-105: the selected type lives in the URL so a view is linkable and
  // survives a refresh — "here's our Starlink fleet" should be a shareable link,
  // not a click path. An unknown value — including `?type=all` from a link
  // saved before KAN-110 removed that tab — falls back to Crew SIMs.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawType = searchParams.get('type');
  const activeTypeKey: InventoryTypeKey = isValidTypeKey(rawType) ? rawType : DEFAULT_TYPE_KEY;
  const activeType = getInventoryType(activeTypeKey);

  const setActiveTypeKey = (key: InventoryTypeKey) => {
    const next = new URLSearchParams(searchParams);
    if (key === DEFAULT_TYPE_KEY) {
      next.delete('type');
    } else {
      next.set('type', key);
    }
    // Selecting a type is navigation within the page, not a new history entry —
    // Back should leave the inventory, not step through tab clicks.
    setSearchParams(next, { replace: true });
    setSelectedRowKeys([]);
    setSelectedSims([]);
  };

  const [sims, setSims] = useState<UnifiedSim[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [onlineFilter, setOnlineFilter] = useState<string | undefined>(undefined);
  const [allocationFilter, setAllocationFilter] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  // KAN-94: eSIM provisioning modal (reseller_admin+).
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);

  // Terminate confirmation modal
  const [terminateModalVisible, setTerminateModalVisible] = useState(false);
  const [terminatingSim, setTerminatingSim] = useState<UnifiedSim | null>(null);
  const [terminateConfirmText, setTerminateConfirmText] = useState('');
  const [terminateIdText, setTerminateIdText] = useState('');

  // Allocate modal
  const [allocateModalVisible, setAllocateModalVisible] = useState(false);
  const [allocatingSim, setAllocatingSim] = useState<UnifiedSim | null>(null);
  const [allocateForm] = Form.useForm();

  // Assign modal
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assigningSim, setAssigningSim] = useState<UnifiedSim | null>(null);
  const [assignMode, setAssignMode] = useState<'asset' | 'user'>('asset');
  const [assignForm] = Form.useForm();

  // Bulk selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSims, setSelectedSims] = useState<UnifiedSim[]>([]);

  // Bulk allocate modal
  const [bulkAllocateVisible, setBulkAllocateVisible] = useState(false);
  const [bulkAllocateForm] = Form.useForm();
  const [bulkProgress, setBulkProgress] = useState<{
    running: boolean;
    current: number;
    total: number;
    successes: number;
    errors: string[];
  }>({ running: false, current: 0, total: 0, successes: 0, errors: [] });

  // Bulk assign-to-asset modal
  const [bulkAssignAssetVisible, setBulkAssignAssetVisible] = useState(false);
  const [bulkAssignAssetForm] = Form.useForm();

  // Everything is fetched once, unfiltered by type, so the tab counts can show
  // the whole picture without one request per type. Type narrowing is a cheap
  // client-side predicate over an already-loaded list.
  const fetchSims = async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getSims({ search: searchText || undefined });
      if (signal?.aborted) return;
      setSims(data);
    } catch (err: any) {
      if (signal?.aborted) return;
      const msg = err?.message || 'Failed to load inventory';
      setLoadError(msg);
      message.error(msg);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [orgs, assetList, userList] = await Promise.all([
        getOrganizations(),
        getAssets(),
        getUsers(),
      ]);
      setOrganizations(orgs);
      setAssets(assetList);
      setUsers(userList);
    } catch {
      // Silently fail
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchSims(controller.signal);
    fetchDropdownData();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filters that only narrow an already-loaded list are reset when the type
  // changes — a status filter carried into the Starlink tab would silently
  // hide rows under a control that is no longer on screen.
  useEffect(() => {
    setStatusFilter(undefined);
    setOnlineFilter(undefined);
    setAllocationFilter(undefined);
  }, [activeTypeKey]);

  const handleSearch = () => {
    fetchSims();
  };

  const typeCounts = useMemo(() => countByType(sims), [sims]);

  // Status choices reflect what the active type actually holds (VRS lifecycle:
  // active, activation-ready, assigned, suspended, cooldown, terminated…), so a
  // hard-coded list can't silently hide a state.
  const statusOptions = useMemo(() => {
    const seen = new Set(
      sims
        .filter(activeType.predicate)
        .map(s => s.status)
        .filter(Boolean)
    );
    return Array.from(seen)
      .sort()
      .map(value => ({ value, label: value }));
  }, [sims, activeType]);

  // Type predicate first, then the per-type filters.
  const visibleSims = useMemo(() => {
    let rows = sims.filter(activeType.predicate);

    if (statusFilter) {
      rows = rows.filter(s => s.status === statusFilter);
    }
    if (onlineFilter) {
      rows = rows.filter(s => (onlineFilter === 'online' ? s.isOnline : !s.isOnline));
    }
    if (allocationFilter) {
      rows = rows.filter(s => (allocationFilter === 'allocated' ? !!s.allocation : !s.allocation));
    }
    return rows;
  }, [sims, activeType, statusFilter, onlineFilter, allocationFilter]);

  // Friendly name optimistic update on inline edit save
  const applyFriendlyNameUpdate = (assignmentId: number, next: string | null) => {
    setSims(prev =>
      prev.map(s =>
        s.allocation?.assignmentId === assignmentId
          ? { ...s, allocation: { ...s.allocation, friendlyName: next } }
          : s
      )
    );
  };

  // Allocate
  const openAllocateModal = (sim: UnifiedSim) => {
    setAllocatingSim(sim);
    allocateForm.resetFields();
    setAllocateModalVisible(true);
  };

  const handleAllocate = async (values: any) => {
    if (!allocatingSim) return;
    setSubmitting(true);
    try {
      await allocateSim({
        identifier: allocatingSim.identifier,
        provider: allocatingSim.provider,
        organizationId: values.organizationId,
      });
      message.success('SIM allocated successfully');
      setAllocateModalVisible(false);
      setAllocatingSim(null);
      fetchSims();
    } catch (err: any) {
      message.error(err.message || 'Failed to allocate SIM');
    } finally {
      setSubmitting(false);
    }
  };

  // Assign
  const openAssignModal = (sim: UnifiedSim) => {
    setAssigningSim(sim);
    setAssignMode('asset');
    assignForm.resetFields();
    setAssignModalVisible(true);
  };

  const handleAssign = async (values: any) => {
    if (!assigningSim) return;
    setSubmitting(true);
    try {
      if (assignMode === 'asset') {
        await assignSimToAsset({
          identifier: assigningSim.identifier,
          provider: assigningSim.provider,
          assetId: values.assetId,
        });
      } else {
        await assignSimToUser({
          identifier: assigningSim.identifier,
          provider: assigningSim.provider,
          userId: values.userId,
          // KAN-105: sim_type classifies a SIM and has no meaning for a
          // Starlink terminal — the backend rejects it for terminals.
          ...(assigningSim.provider === 'clarus' ? {} : { simType: values.simType }),
        });
      }
      message.success(
        assigningSim.provider === 'clarus'
          ? 'Terminal assigned successfully'
          : 'SIM assigned successfully'
      );
      setAssignModalVisible(false);
      setAssigningSim(null);
      fetchSims();
    } catch (err: any) {
      message.error(err.message || 'Failed to assign');
    } finally {
      setSubmitting(false);
    }
  };

  // Unassign (remove asset/user but keep org allocation)
  const handleUnassign = async (assignmentId: number) => {
    try {
      await removeSimAssignment(assignmentId);
      message.success('SIM unassigned successfully');
      fetchSims();
    } catch (err: any) {
      message.error(err.message || 'Failed to unassign SIM');
    }
  };

  // Deallocate (fully remove from org, return to unallocated pool)
  const handleDeallocate = async (assignmentId: number) => {
    try {
      await deallocateSim(assignmentId);
      message.success('SIM deallocated successfully');
      fetchSims();
    } catch (err: any) {
      message.error(err.message || 'Failed to deallocate SIM');
    }
  };

  // SIM operations (activate/suspend/resume)
  const handleSimOperation = async (
    sim: UnifiedSim,
    operation: 'activate' | 'suspend' | 'resume'
  ) => {
    try {
      await simOperation(sim.provider, sim.identifier, operation);
      message.success(`SIM ${operation} successful`);
      fetchSims();
    } catch (err: any) {
      message.error(err.message || `Failed to ${operation} SIM`);
    }
  };

  // Terminate (requires special confirmation)
  const openTerminateModal = (sim: UnifiedSim) => {
    setTerminatingSim(sim);
    setTerminateConfirmText('');
    setTerminateIdText('');
    setTerminateModalVisible(true);
  };

  const handleTerminate = async () => {
    if (!terminatingSim) return;
    try {
      await simOperation(terminatingSim.provider, terminatingSim.identifier, 'terminate');
      message.success('SIM terminated permanently');
      setTerminateModalVisible(false);
      setTerminatingSim(null);
      fetchSims();
    } catch (err: any) {
      message.error(err.message || 'Failed to terminate SIM');
    }
  };

  const terminateValid =
    terminateConfirmText === 'terminate' &&
    terminatingSim &&
    terminateIdText === terminatingSim.identifier;

  // Bulk allocate
  const openBulkAllocateModal = () => {
    bulkAllocateForm.resetFields();
    setBulkProgress({ running: false, current: 0, total: 0, successes: 0, errors: [] });
    setBulkAllocateVisible(true);
  };

  const handleBulkAllocate = async (values: any) => {
    const toAllocate = selectedSims.filter(s => !s.allocation);
    const totalCount = toAllocate.length;

    setBulkProgress({ running: true, current: 0, total: totalCount, successes: 0, errors: [] });

    let successes = 0;
    const errors: string[] = [];

    for (let i = 0; i < toAllocate.length; i++) {
      const sim = toAllocate[i];
      try {
        await allocateSim({
          identifier: sim.identifier,
          provider: sim.provider,
          organizationId: values.organizationId,
        });
        successes++;
      } catch (err: any) {
        errors.push(`${sim.provider}/${sim.identifier}: ${err.message || 'Failed'}`);
      }
      setBulkProgress({
        running: i < toAllocate.length - 1,
        current: i + 1,
        total: totalCount,
        successes,
        errors: [...errors],
      });
    }

    setBulkProgress(prev => ({ ...prev, running: false }));

    if (successes > 0) {
      message.success(`Successfully allocated ${successes} of ${totalCount} SIMs`);
    }
    if (errors.length > 0) {
      message.error(`${errors.length} allocation(s) failed`);
    }

    setSelectedRowKeys([]);
    setSelectedSims([]);
    fetchSims();
  };

  // Bulk assign-to-asset
  const openBulkAssignAssetModal = () => {
    bulkAssignAssetForm.resetFields();
    setBulkProgress({ running: false, current: 0, total: 0, successes: 0, errors: [] });
    setBulkAssignAssetVisible(true);
  };

  const handleBulkAssignAsset = async (values: any) => {
    // Only SIMs already allocated and not yet assigned to an asset/user
    const toAssign = selectedSims.filter(
      s => s.allocation && !s.allocation.assetId && !s.allocation.userId
    );
    const totalCount = toAssign.length;

    setBulkProgress({ running: true, current: 0, total: totalCount, successes: 0, errors: [] });

    let successes = 0;
    const errors: string[] = [];

    for (let i = 0; i < toAssign.length; i++) {
      const sim = toAssign[i];
      try {
        await assignSimToAsset({
          identifier: sim.identifier,
          provider: sim.provider,
          assetId: values.assetId,
        });
        successes++;
      } catch (err: any) {
        errors.push(`${sim.provider}/${sim.identifier}: ${err.message || 'Failed'}`);
      }
      setBulkProgress({
        running: i < toAssign.length - 1,
        current: i + 1,
        total: totalCount,
        successes,
        errors: [...errors],
      });
    }

    setBulkProgress(prev => ({ ...prev, running: false }));

    if (successes > 0) {
      message.success(`Successfully assigned ${successes} of ${totalCount} SIMs to asset`);
    }
    if (errors.length > 0) {
      message.error(`${errors.length} assignment(s) failed`);
    }

    setSelectedRowKeys([]);
    setSelectedSims([]);
    fetchSims();
  };

  const providerColor = (provider: string) => {
    switch (provider) {
      case 'vrs':
        return 'blue';
      case 'giacom':
        return 'green';
      case 'clarus':
        return 'purple';
      default:
        return 'default';
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'activation-ready':
        return 'blue';
      case 'suspended':
      case 'cooldown':
        return 'orange';
      case 'deactivated':
      case 'terminated':
        return 'red';
      default:
        return 'default';
    }
  };

  const rowKey = (record: UnifiedSim) => `${record.provider}-${record.identifier}`;

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: UnifiedSim[]) => {
      setSelectedRowKeys(keys);
      setSelectedSims(rows);
    },
    getCheckboxProps: (record: UnifiedSim) => ({
      // Disable only fully-assigned SIMs (already linked to an asset or user).
      // Unallocated SIMs are selectable for bulk Allocate; org-allocated-but-unassigned
      // SIMs are selectable for bulk Assign-to-Asset.
      disabled: !!(record.allocation && (record.allocation.assetId || record.allocation.userId)),
    }),
  };

  const unallocatedSelectedCount = selectedSims.filter(s => !s.allocation).length;
  const assignableSelectedCount = selectedSims.filter(
    s => s.allocation && !s.allocation.assetId && !s.allocation.userId
  ).length;
  const distinctAssignableOrgIds = Array.from(
    new Set(
      selectedSims
        .filter(s => s.allocation && !s.allocation.assetId && !s.allocation.userId)
        .map(s => s.allocation!.organizationId)
    )
  );

  // KAN-105: columns are a keyed registry rather than one fixed array. Each
  // inventory type names the columns it wants (see inventoryTypes.ts) and the
  // table is assembled from that, so adding a fourth connectivity type is a
  // data change rather than another branch in here.
  //
  // Column-header filters were dropped in favour of the filter bar above the
  // table: with per-type column sets a header filter would vanish when the type
  // changed, silently taking its narrowing with it.
  const COLUMN_REGISTRY: Record<string, any> = {
    identifier: {
      title: 'Identifier',
      dataIndex: 'identifier',
      key: 'identifier',
      sorter: (a: UnifiedSim, b: UnifiedSim) => a.identifier.localeCompare(b.identifier),
      render: (id: string, record: UnifiedSim) =>
        record.provider === 'vrs' ? (
          <Link to={`/admin/sims/${record.provider}/${id}`}>{id}</Link>
        ) : record.provider === 'clarus' ? (
          <Link to={`/admin/terminals/${encodeURIComponent(id)}`}>{id}</Link>
        ) : (
          id
        ),
    },
    msisdn: {
      title: 'MSISDN',
      dataIndex: 'msisdn',
      key: 'msisdn',
      sorter: (a: UnifiedSim, b: UnifiedSim) => (a.msisdn || '').localeCompare(b.msisdn || ''),
      render: (msisdn: string | null) => msisdn || '—',
    },
    status: {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a: UnifiedSim, b: UnifiedSim) => a.status.localeCompare(b.status),
      render: (status: string) => <Tag color={statusColor(status)}>{status}</Tag>,
    },
    alias: {
      title: 'Alias',
      dataIndex: 'alias',
      key: 'alias',
      sorter: (a: UnifiedSim, b: UnifiedSim) => (a.alias || '').localeCompare(b.alias || ''),
      render: (alias: string | null) => alias || '—',
    },
    eid: {
      title: 'eUICC ID',
      dataIndex: 'eid',
      key: 'eid',
      ellipsis: true,
      sorter: (a: UnifiedSim, b: UnifiedSim) => (a.eid || '').localeCompare(b.eid || ''),
      render: (eid: string | null | undefined) => eid || '—',
    },
    rspState: {
      title: 'RSP State',
      dataIndex: 'rspState',
      key: 'rspState',
      width: 140,
      render: (state: string | null | undefined) => (state ? <Tag>{state}</Tag> : '—'),
    },
    customReference: {
      title: 'Reference',
      dataIndex: 'customReference',
      key: 'customReference',
      ellipsis: true,
      sorter: (a: UnifiedSim, b: UnifiedSim) =>
        (a.customReference || '').localeCompare(b.customReference || ''),
      render: (ref: string | null | undefined) => ref || '—',
    },

    // --- Starlink-specific columns (KAN-105) ------------------------------
    deviceId: {
      title: 'Device ID',
      dataIndex: 'identifier',
      key: 'deviceId',
      sorter: (a: UnifiedSim, b: UnifiedSim) => a.identifier.localeCompare(b.identifier),
      render: (id: string) => <Link to={`/admin/terminals/${encodeURIComponent(id)}`}>{id}</Link>,
    },
    kitSerial: {
      title: 'Kit S/N',
      key: 'kitSerial',
      width: 170,
      sorter: (a: UnifiedSim, b: UnifiedSim) =>
        (a.kitSerialNumber || '').localeCompare(b.kitSerialNumber || ''),
      render: (_: any, record: UnifiedSim) => record.kitSerialNumber || '—',
    },
    site: {
      title: 'Site',
      key: 'site',
      ellipsis: true,
      sorter: (a: UnifiedSim, b: UnifiedSim) => (a.siteName || '').localeCompare(b.siteName || ''),
      render: (_: any, record: UnifiedSim) => record.siteName || '—',
    },
    serviceLine: {
      title: 'Service Line',
      key: 'serviceLine',
      ellipsis: true,
      sorter: (a: UnifiedSim, b: UnifiedSim) =>
        (a.serviceLineNumber || '').localeCompare(b.serviceLineNumber || ''),
      render: (_: any, record: UnifiedSim) => record.serviceLineNumber || '—',
    },
    online: {
      title: 'Online',
      key: 'online',
      width: 150,
      sorter: (a: UnifiedSim, b: UnifiedSim) => Number(!!a.isOnline) - Number(!!b.isOnline),
      render: (_: any, record: UnifiedSim) => {
        const alerts = record.activeAlerts || [];
        const badge = (
          <Badge
            status={record.isOnline ? 'success' : 'default'}
            text={record.isOnline ? 'Online' : 'Offline'}
          />
        );
        // A terminal can be online and still reporting an obstruction or a
        // pending reboot, so alerts sit alongside the reachability state.
        return (
          <Space size={4}>
            {record.lastTelemetryAt ? (
              <Tooltip title={`Last reading ${new Date(record.lastTelemetryAt).toLocaleString()}`}>
                {badge}
              </Tooltip>
            ) : (
              badge
            )}
            {alerts.length > 0 && (
              <Tooltip title={alerts.map(a => a.label).join(', ')}>
                <Tag color="orange">{alerts.length}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },

    friendlyName: {
      title: 'Friendly Name',
      key: 'friendlyName',
      width: 200,
      sorter: (a: UnifiedSim, b: UnifiedSim) =>
        (a.allocation?.friendlyName || '').localeCompare(b.allocation?.friendlyName || ''),
      render: (_: any, record: UnifiedSim) => (
        <EditableFriendlyName
          assignmentId={record.allocation?.assignmentId}
          value={record.allocation?.friendlyName}
          canEdit={canEditFriendlyName}
          onSaved={next =>
            record.allocation && applyFriendlyNameUpdate(record.allocation.assignmentId, next)
          }
        />
      ),
    },
    createdAt: {
      // KAN-94: when the SIM entered our DB. For a provisioned eSIM this is the
      // provisioning time — sort descending to find a freshly provisioned eSIM.
      title: 'Added',
      key: 'createdAt',
      width: 170,
      sorter: (a: UnifiedSim, b: UnifiedSim) =>
        (a.createdAt || '').localeCompare(b.createdAt || ''),
      render: (_: any, record: UnifiedSim) =>
        record.createdAt ? new Date(record.createdAt).toLocaleString() : '—',
    },
    allocatedTo: {
      title: 'Allocated To',
      key: 'allocatedTo',
      sorter: (a: UnifiedSim, b: UnifiedSim) =>
        (a.allocation?.orgName || '').localeCompare(b.allocation?.orgName || ''),
      render: (_: any, record: UnifiedSim) => (record.allocation ? record.allocation.orgName : '—'),
    },
    assignedTo: {
      title: 'Assigned To',
      key: 'assignedTo',
      render: (_: any, record: UnifiedSim) => {
        if (!record.allocation) return '\u2014';
        const parts: React.ReactNode[] = [];
        if (record.allocation.assetName) {
          parts.push(
            <Link key="asset" to={`/admin/assets/${record.allocation.assetId}`}>
              {record.allocation.assetName}
            </Link>
          );
        }
        if (record.allocation.userName) {
          parts.push(
            <span key="user" style={{ color: '#722ed1' }}>
              {parts.length > 0 ? ' / ' : ''}
              <Link to={`/admin/users/${record.allocation.userId}`}>
                {record.allocation.userName}
              </Link>
            </span>
          );
        }
        if (record.allocation.simType) {
          parts.push(
            <Tag key="type" color="purple" style={{ marginLeft: 4 }}>
              {record.allocation.simType}
            </Tag>
          );
        }
        return parts.length > 0 ? <>{parts}</> : '\u2014';
      },
    },
    actions: {
      title: 'Actions',
      key: 'actions',
      className: 'col-actions',
      width: 80,
      render: (_: any, record: UnifiedSim) => {
        // Non-destructive items go first; destructive items go after a
        // divider. RBAC visibility (canOperate + provider === 'vrs') is
        // preserved exactly as before.
        const safe: NonNullable<MenuProps['items']> = [];
        const danger: NonNullable<MenuProps['items']> = [];
        // KAN-105: a Starlink terminal is not a SIM — say so in the prompts,
        // which are the only place the user sees the word.
        const Noun = record.provider === 'clarus' ? 'Terminal' : 'SIM';

        if (!record.allocation) {
          safe.push({
            key: 'alloc',
            label: 'Allocate',
            onClick: () => openAllocateModal(record),
          });
        } else if (!record.allocation.assetId && !record.allocation.userId) {
          safe.push({
            key: 'assign',
            label: 'Assign',
            onClick: () => openAssignModal(record),
          });
          danger.push({
            key: 'dealloc',
            label: 'Deallocate',
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: `Deallocate ${Noun}`,
                content: `Remove ${record.identifier} from the organization?`,
                okText: 'Deallocate',
                okButtonProps: { danger: true },
                onOk: () => handleDeallocate(record.allocation!.assignmentId),
              });
            },
          });
        } else {
          danger.push({
            key: 'unassign',
            label: 'Unassign',
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: `Unassign ${Noun}`,
                content: `Remove ${record.identifier}'s current asset/user assignment?`,
                okText: 'Unassign',
                okButtonProps: { danger: true },
                onOk: () => handleUnassign(record.allocation!.assignmentId),
              });
            },
          });
        }

        if (canOperate && record.provider === 'vrs') {
          if (record.status === 'suspended') {
            safe.push({
              key: 'resume',
              label: 'Resume',
              onClick: () => {
                Modal.confirm({
                  title: 'Resume SIM',
                  content: `Reactivate suspended SIM ${record.identifier}?`,
                  okText: 'Resume',
                  onOk: () => handleSimOperation(record, 'resume'),
                });
              },
            });
          }
          if (record.status === 'active') {
            safe.push({
              key: 'suspend',
              label: 'Suspend',
              onClick: () => {
                Modal.confirm({
                  title: 'Suspend SIM',
                  content: `Suspend active SIM ${record.identifier}?`,
                  okText: 'Suspend',
                  okButtonProps: { danger: true },
                  onOk: () => handleSimOperation(record, 'suspend'),
                });
              },
            });
          }
          if (record.status !== 'active' && record.status !== 'suspended') {
            safe.push({
              key: 'activate',
              label: 'Activate',
              onClick: () => {
                Modal.confirm({
                  title: 'Activate SIM',
                  content: `Activate SIM ${record.identifier}?`,
                  okText: 'Activate',
                  onOk: () => handleSimOperation(record, 'activate'),
                });
              },
            });
          }
          danger.push({
            key: 'terminate',
            label: 'Terminate',
            danger: true,
            onClick: () => openTerminateModal(record),
          });
        }

        const items: MenuProps['items'] = [
          ...safe,
          ...(safe.length > 0 && danger.length > 0 ? [{ type: 'divider' as const }] : []),
          ...danger,
        ];

        if (items.length === 0) {
          return <span style={{ color: '#bbb' }}>—</span>;
        }

        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label="SIM actions" />
          </Dropdown>
        );
      },
    },
  };

  // The active type names its columns; unknown keys are dropped rather than
  // rendering an undefined column.
  const columns = useMemo(
    () => activeType.columns.map(key => COLUMN_REGISTRY[key]).filter(Boolean),
    // COLUMN_REGISTRY closes over handlers and state that change every render;
    // rebuilding on type change is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeType, sims, canEditFriendlyName, canOperate]
  );

  const cardHandlers: SimListCardHandlers = {
    onAllocate: openAllocateModal,
    onAssign: openAssignModal,
    onUnassign: handleUnassign,
    onDeallocate: handleDeallocate,
    onSimOperation: handleSimOperation,
    onTerminate: openTerminateModal,
  };

  // Tab labels carry their counts so the page answers "what do we own" without
  // clicking through each type.
  const segmentedOptions = INVENTORY_TYPES.map(t => ({
    value: t.key,
    label: (
      <Tooltip title={t.description} placement="bottom">
        <span>
          {t.label}{' '}
          <Tag style={{ marginInlineEnd: 0 }} color={t.key === activeTypeKey ? 'blue' : 'default'}>
            {typeCounts[t.key] ?? 0}
          </Tag>
        </span>
      </Tooltip>
    ),
  }));

  return (
    <div className="page-wrapper" style={{ padding: 24 }}>
      <PageHeader
        title="Connectivity Inventory"
        exportConfig={{
          filename: () => {
            const date = new Date().toISOString().split('T')[0];
            return `MIMO-Inventory-${activeType.key}-${date}`;
          },
          disabled: loading,
          onBeforePrint: () => setPrintMode(true),
          onAfterPrint: () => setPrintMode(false),
          prePrintDelayMs: 500,
        }}
        actions={
          <Space>
            {canOperate && (
              <>
                {/* KAN-167: the guide lives next to the action it explains, not in the menu. */}
                <EsimGuideLink>eSIM guide</EsimGuideLink>
                <Button type="primary" onClick={() => setProvisionModalOpen(true)}>
                  Provision eSIM
                </Button>
              </>
            )}
            <PageHelp
              title="Inventory Help"
              sections={[
                {
                  title: 'SIM Card Lifecycle',
                  content:
                    'SIMs go through these stages:\n\n1. Unallocated \u2014 not assigned to any org\n2. Allocated \u2014 assigned to an org, but not to a specific asset/user\n3. Assigned \u2014 linked to an asset (yacht SIM) or user (crew SIM)\n\nUse Allocate \u2192 Assign for the full flow.',
                },
                {
                  title: 'Actions',
                  content:
                    '- Allocate: assign SIM to an organization\n- Assign: link to an asset or user (after allocation)\n- Unassign: remove asset/user link, keep org allocation\n- Deallocate: fully remove from org, return to pool\n- Suspend/Resume: pause or reactivate connectivity\n- Terminate: permanently deactivate (irreversible)',
                  tips: [
                    'Use bulk select (checkboxes) to allocate multiple SIMs at once',
                    'Click an identifier to see full SIM details and usage',
                  ],
                },
                {
                  title: 'Connectivity Types',
                  content:
                    'Use the type selector at the top of the page to switch between:\n\n- Crew SIMs — UK mobile SIMs for crew phones\n- Data SIMs — physical maritime data SIMs for vessel connectivity\n- eSIMs — downloadable profiles, including Travel eSIMs\n- Starlink — Starlink terminals, with online state and live telemetry\n\nEach type shows the columns and filters that apply to it, so nothing is padded with blank cells.',
                  tips: [
                    'The selected type is in the page URL — copy the link to share that exact view',
                    'The number on each tab is how many of that type exist',
                  ],
                },
                {
                  title: 'Starlink Terminals',
                  content:
                    'Terminals are allocated and assigned exactly like SIMs: allocate to an organization, then assign to a vessel or to a portal user.\n\nOnline state and telemetry refresh hourly from Clarus. Open a terminal to see live telemetry, active alerts and data usage.\n\nThe Clarus API is read-only, so terminals have no suspend/resume/terminate actions.',
                },
              ]}
            />
          </Space>
        }
      />
      <div className="print-only">
        <h2 className="print-page-title">MIMO Connect — Connectivity Inventory</h2>
        <p className="print-meta">Generated {new Date().toLocaleString()}</p>
        <div className="print-filter-summary">
          <strong>Type:</strong> {activeType.label} ({visibleSims.length})
          {searchText && (
            <>
              {' | '}
              <strong>Search:</strong> "{searchText}"
            </>
          )}
        </div>
      </div>

      {/*
        KAN-105: the type selector. Scrolls horizontally on narrow screens
        rather than wrapping, so the tabs stay on one line and keep reading as
        a single control.
      */}
      <div className="no-print" style={{ marginBottom: 16, overflowX: 'auto' }}>
        <Segmented
          block={!!screens.md}
          value={activeTypeKey}
          onChange={value => setActiveTypeKey(value as InventoryTypeKey)}
          options={segmentedOptions}
        />
      </div>

      {loadError && (
        <Alert
          className="no-print"
          style={{ marginBottom: 16 }}
          type="error"
          showIcon
          message="Could not load inventory"
          description={loadError}
          action={
            <Button size="small" onClick={() => fetchSims()}>
              Retry
            </Button>
          }
        />
      )}

      {/* Only the filters that mean something for the active type. */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} className="no-print">
        <Col xs={24} sm={16} md={12} lg={9}>
          <Input.Search
            placeholder={activeType.searchPlaceholder}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onSearch={handleSearch}
            allowClear
          />
        </Col>
        {activeType.filters.includes('status') && (
          <Col xs={12} sm={8} md={6} lg={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
            />
          </Col>
        )}
        {activeType.filters.includes('online') && (
          <Col xs={12} sm={8} md={6} lg={4}>
            <Select
              placeholder="Online state"
              allowClear
              style={{ width: '100%' }}
              value={onlineFilter}
              onChange={setOnlineFilter}
              options={[
                { value: 'online', label: 'Online' },
                { value: 'offline', label: 'Offline' },
              ]}
            />
          </Col>
        )}
        {activeType.filters.includes('allocation') && (
          <Col xs={12} sm={8} md={6} lg={4}>
            <Select
              placeholder="Allocation"
              allowClear
              style={{ width: '100%' }}
              value={allocationFilter}
              onChange={setAllocationFilter}
              options={[
                { value: 'allocated', label: 'Allocated' },
                { value: 'unallocated', label: 'Unallocated' },
              ]}
            />
          </Col>
        )}
      </Row>
      {selectedRowKeys.length > 0 && (
        <div className="no-print">
          <Alert
            style={{ marginBottom: 16 }}
            message={
              <Space wrap size="small">
                <span>
                  {selectedRowKeys.length} {activeTypeKey === 'starlink' ? 'terminal(s)' : 'SIM(s)'}{' '}
                  selected
                </span>
                <Button
                  type="primary"
                  size="small"
                  disabled={unallocatedSelectedCount === 0}
                  onClick={openBulkAllocateModal}
                  title={
                    unallocatedSelectedCount === 0
                      ? 'No unallocated SIMs in selection'
                      : `Allocate ${unallocatedSelectedCount} unallocated SIM(s)`
                  }
                >
                  Allocate ({unallocatedSelectedCount})
                </Button>
                <Button
                  size="small"
                  disabled={assignableSelectedCount === 0 || distinctAssignableOrgIds.length > 1}
                  onClick={openBulkAssignAssetModal}
                  title={
                    assignableSelectedCount === 0
                      ? 'No allocated-but-unassigned SIMs in selection'
                      : distinctAssignableOrgIds.length > 1
                        ? 'Selected SIMs span multiple organizations — narrow your selection'
                        : `Assign ${assignableSelectedCount} SIM(s) to an asset`
                  }
                >
                  Assign to Asset ({assignableSelectedCount})
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedRowKeys([]);
                    setSelectedSims([]);
                  }}
                >
                  Clear Selection
                </Button>
              </Space>
            }
            type="info"
            showIcon
          />
        </div>
      )}
      <ResponsiveTableOrCards
        dataSource={visibleSims}
        rowKey={rowKey}
        columns={columns}
        loading={loading}
        rowSelection={rowSelection}
        breakpoint="xl"
        pagination={
          printMode
            ? false
            : {
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }
        }
        renderCard={(sim, helpers) => (
          <SimListCard
            sim={sim}
            canOperate={canOperate}
            selected={helpers.selected}
            selectDisabled={helpers.selectDisabled}
            onSelectChange={helpers.onSelectChange}
            handlers={cardHandlers}
          />
        )}
        emptyText="No SIMs found"
      />
      {/* Allocate Modal */}
      <ResponsiveModal
        title={`Allocate ${allocatingSim?.provider === 'clarus' ? 'Terminal' : 'SIM'}: ${allocatingSim?.identifier || ''}`}
        open={allocateModalVisible}
        onCancel={() => {
          setAllocateModalVisible(false);
          setAllocatingSim(null);
        }}
        footer={null}
      >
        <Form form={allocateForm} layout="vertical" onFinish={handleAllocate}>
          <Form.Item
            name="organizationId"
            label="Organization"
            rules={[{ required: true, message: 'Please select an organization' }]}
          >
            <Select placeholder="Select organization">
              {organizations.map(org => (
                <Select.Option key={org.id} value={org.id}>
                  {org.name} ({org.type})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Allocate
              </Button>
              <Button
                onClick={() => {
                  setAllocateModalVisible(false);
                  setAllocatingSim(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>
      {/* Assign Modal */}
      <ResponsiveModal
        title={`Assign ${assigningSim?.provider === 'clarus' ? 'Terminal' : 'SIM'}: ${assigningSim?.identifier || ''}`}
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setAssigningSim(null);
        }}
        footer={null}
      >
        <Radio.Group
          value={assignMode}
          onChange={e => {
            setAssignMode(e.target.value);
            assignForm.resetFields();
          }}
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="asset">To Asset</Radio.Button>
          <Radio.Button value="user">To User</Radio.Button>
        </Radio.Group>

        <Form form={assignForm} layout="vertical" onFinish={handleAssign}>
          {assignMode === 'asset' ? (
            <Form.Item
              name="assetId"
              label="Asset"
              rules={[{ required: true, message: 'Please select an asset' }]}
            >
              <Select placeholder="Select asset" showSearch optionFilterProp="children">
                {assets
                  .filter(
                    a =>
                      a.is_active && assigningSim?.allocation?.organizationId === a.organization_id
                  )
                  .map(asset => (
                    <Select.Option key={asset.id} value={asset.id}>
                      {asset.name} ({asset.asset_type || 'no type'})
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="userId"
                label="User"
                rules={[{ required: true, message: 'Please select a user' }]}
              >
                <Select placeholder="Select user" showSearch optionFilterProp="children">
                  {users
                    .filter(
                      u =>
                        u.is_active &&
                        assigningSim?.allocation?.organizationId === u.organization_id
                    )
                    .map(user => (
                      <Select.Option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
              {/*
                KAN-105: SIM Type classifies a SIM (yacht/crew/personal) and is
                meaningless for a Starlink terminal, so it is hidden — and not
                required — when assigning one.
              */}
              {assigningSim?.provider !== 'clarus' && (
                <Form.Item
                  name="simType"
                  label="SIM Type"
                  rules={[{ required: true, message: 'Please select a SIM type' }]}
                >
                  <LookupSelect category="sim_type" placeholder="Select or enter SIM type" />
                </Form.Item>
              )}
            </>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Assign
              </Button>
              <Button
                onClick={() => {
                  setAssignModalVisible(false);
                  setAssigningSim(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>
      {/* Bulk Allocate Modal */}
      <ResponsiveModal
        title="Bulk Allocate SIMs"
        open={bulkAllocateVisible}
        onCancel={() => {
          if (!bulkProgress.running) {
            setBulkAllocateVisible(false);
          }
        }}
        footer={null}
        closable={!bulkProgress.running}
        maskClosable={!bulkProgress.running}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>{selectedSims.filter(s => !s.allocation).length} SIM(s) to allocate:</strong>
          <div
            style={{
              maxHeight: 150,
              overflow: 'auto',
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: 8,
              marginTop: 8,
              fontSize: 12,
            }}
          >
            {selectedSims
              .filter(s => !s.allocation)
              .map(sim => (
                <div key={`${sim.provider}-${sim.identifier}`}>
                  <Tag color={providerColor(sim.provider)}>{providerLabel(sim.provider)}</Tag>
                  {sim.identifier}
                </div>
              ))}
          </div>
        </div>

        <Form form={bulkAllocateForm} layout="vertical" onFinish={handleBulkAllocate}>
          <Form.Item
            name="organizationId"
            label="Organization"
            rules={[{ required: true, message: 'Please select an organization' }]}
          >
            <Select placeholder="Select organization" disabled={bulkProgress.running}>
              {organizations.map(org => (
                <Select.Option key={org.id} value={org.id}>
                  {org.name} ({org.type})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {bulkProgress.running || bulkProgress.current > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <Progress
                percent={Math.round((bulkProgress.current / bulkProgress.total) * 100)}
                status={
                  bulkProgress.running
                    ? 'active'
                    : bulkProgress.errors.length > 0
                      ? 'exception'
                      : 'success'
                }
              />
              <div style={{ marginTop: 4 }}>
                {bulkProgress.running
                  ? `Allocating ${bulkProgress.current} of ${bulkProgress.total}...`
                  : `Done: ${bulkProgress.successes} succeeded, ${bulkProgress.errors.length} failed`}
              </div>
              {bulkProgress.errors.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 100,
                    overflow: 'auto',
                    color: '#ff4d4f',
                    fontSize: 12,
                  }}
                >
                  {bulkProgress.errors.map((e, i) => (
                    <div key={i}>{e}</div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <Form.Item>
            <Space>
              {!bulkProgress.running && bulkProgress.current === 0 && (
                <Button type="primary" htmlType="submit">
                  Allocate All
                </Button>
              )}
              <Button onClick={() => setBulkAllocateVisible(false)} disabled={bulkProgress.running}>
                {bulkProgress.current > 0 && !bulkProgress.running ? 'Close' : 'Cancel'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>
      {/* Bulk Assign-to-Asset Modal */}
      <ResponsiveModal
        title="Bulk Assign SIMs to Asset"
        open={bulkAssignAssetVisible}
        onCancel={() => {
          if (!bulkProgress.running) {
            setBulkAssignAssetVisible(false);
          }
        }}
        footer={null}
        closable={!bulkProgress.running}
        maskClosable={!bulkProgress.running}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>{assignableSelectedCount} SIM(s) to assign:</strong>
          <div
            style={{
              maxHeight: 150,
              overflow: 'auto',
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              padding: 8,
              marginTop: 8,
              fontSize: 12,
            }}
          >
            {selectedSims
              .filter(s => s.allocation && !s.allocation.assetId && !s.allocation.userId)
              .map(sim => (
                <div key={`${sim.provider}-${sim.identifier}`}>
                  <Tag color={providerColor(sim.provider)}>{providerLabel(sim.provider)}</Tag>
                  {sim.identifier}
                </div>
              ))}
          </div>
        </div>

        <Form form={bulkAssignAssetForm} layout="vertical" onFinish={handleBulkAssignAsset}>
          <Form.Item
            name="assetId"
            label="Asset"
            rules={[{ required: true, message: 'Please select an asset' }]}
          >
            <Select
              placeholder="Select asset"
              showSearch
              optionFilterProp="children"
              disabled={bulkProgress.running}
            >
              {assets
                .filter(
                  a =>
                    a.is_active &&
                    distinctAssignableOrgIds.length === 1 &&
                    a.organization_id === distinctAssignableOrgIds[0]
                )
                .map(asset => (
                  <Select.Option key={asset.id} value={asset.id}>
                    {asset.name} ({asset.asset_type || 'no type'})
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          {bulkProgress.running || bulkProgress.current > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <Progress
                percent={Math.round((bulkProgress.current / bulkProgress.total) * 100)}
                status={
                  bulkProgress.running
                    ? 'active'
                    : bulkProgress.errors.length > 0
                      ? 'exception'
                      : 'success'
                }
              />
              <div style={{ marginTop: 4 }}>
                {bulkProgress.running
                  ? `Assigning ${bulkProgress.current} of ${bulkProgress.total}...`
                  : `Done: ${bulkProgress.successes} succeeded, ${bulkProgress.errors.length} failed`}
              </div>
              {bulkProgress.errors.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 100,
                    overflow: 'auto',
                    color: '#ff4d4f',
                    fontSize: 12,
                  }}
                >
                  {bulkProgress.errors.map((e, i) => (
                    <div key={i}>{e}</div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <Form.Item>
            <Space>
              {!bulkProgress.running && bulkProgress.current === 0 && (
                <Button type="primary" htmlType="submit">
                  Assign All
                </Button>
              )}
              <Button
                onClick={() => setBulkAssignAssetVisible(false)}
                disabled={bulkProgress.running}
              >
                {bulkProgress.current > 0 && !bulkProgress.running ? 'Close' : 'Cancel'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </ResponsiveModal>
      {/* Terminate Confirmation Modal */}
      <ResponsiveModal
        title={<span style={{ color: '#ff4d4f' }}>Permanently Terminate SIM</span>}
        open={terminateModalVisible}
        onCancel={() => {
          setTerminateModalVisible(false);
          setTerminatingSim(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setTerminateModalVisible(false);
              setTerminatingSim(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="terminate"
            danger
            type="primary"
            disabled={!terminateValid}
            onClick={handleTerminate}
          >
            Permanently Terminate
          </Button>,
        ]}
      >
        <Alert
          message="This action is PERMANENT and IRREVERSIBLE"
          description="The SIM card will be permanently deactivated on the carrier network. It cannot be reactivated after termination."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {terminatingSim && (
          <div>
            <p>
              <strong>SIM:</strong> {terminatingSim.identifier}
            </p>
            <p>
              <strong>Provider:</strong> {providerLabel(terminatingSim.provider)}
            </p>
            <p>
              <strong>MSISDN:</strong> {terminatingSim.msisdn || '—'}
            </p>
            <p>
              <strong>Status:</strong> {terminatingSim.status}
            </p>

            <div style={{ marginTop: 16 }}>
              <p>
                Type <strong>terminate</strong> to confirm:
              </p>
              <Input
                value={terminateConfirmText}
                onChange={e => setTerminateConfirmText(e.target.value)}
                placeholder="terminate"
                status={
                  terminateConfirmText && terminateConfirmText !== 'terminate' ? 'error' : undefined
                }
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <p>
                Type the SIM identifier <strong>{terminatingSim.identifier}</strong> to confirm:
              </p>
              <Input
                value={terminateIdText}
                onChange={e => setTerminateIdText(e.target.value)}
                placeholder={terminatingSim.identifier}
                status={
                  terminateIdText && terminateIdText !== terminatingSim.identifier
                    ? 'error'
                    : undefined
                }
              />
            </div>
          </div>
        )}
      </ResponsiveModal>
      {canOperate && (
        <ProvisionEsimModal
          open={provisionModalOpen}
          onClose={() => setProvisionModalOpen(false)}
          onProvisioned={() => fetchSims()}
        />
      )}
    </div>
  );
};

export default SimInventory;
